import { SHELF_FINISHES, type ShelfPreference } from '@bookclub/shared';
import { Check } from 'lucide-react-native';
import { useTranslation } from 'react-i18next';
import { Pressable, Text, View } from 'react-native';
import { useShelf } from '@/api/preferences';
import { Plank } from '@/components/Bookshelf';
import { Hint } from '@/components/ui/Section';
import { Segmented } from '@/components/ui/Segmented';
import { Sheet } from '@/components/ui/Sheet';
import { shelfMaterials, useTheme } from '@/theme';

/** Heights of the three little books standing on each finish's sample. */
const SAMPLE_BOOKS = [26, 32, 28];

/** Your shelf: covers or spines, and what the boards are made of. Each choice shows at once, behind the sheet. */
export function ShelfSheet({ visible, onClose }: { visible: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const { colors, fonts, fontSize, radius, shelf: style, space } = useTheme();
  const { shelf, change } = useShelf();
  const finish = shelf.finish ?? style.finish;

  return (
    <Sheet visible={visible} onClose={onClose} title={t('shelf.customize')}>
      <View style={{ gap: space.lg, paddingBottom: space.sm }}>
        <View style={{ gap: space.sm }}>
          <Text accessibilityRole="header" aria-level={2} style={{ fontFamily: fonts.heading, fontSize: fontSize.md, color: colors.text }}>
            {t('shelf.books')}
          </Text>
          <Segmented<ShelfPreference['layout']>
            kind="radio"
            label={t('shelf.books')}
            options={[
              { value: 'covers', label: t('shelf.covers') },
              { value: 'spines', label: t('shelf.spines') },
            ]}
            value={shelf.layout}
            onChange={(layout) => change({ layout })}
          />
          {shelf.layout === 'spines' && <Hint>{t('shelf.spinesHint')}</Hint>}
        </View>

        <View style={{ gap: space.sm }}>
          <Text accessibilityRole="header" aria-level={2} style={{ fontFamily: fonts.heading, fontSize: fontSize.md, color: colors.text }}>
            {t('shelf.finish')}
          </Text>
          <View role="radiogroup" aria-label={t('shelf.finish')} style={{ flexDirection: 'row', flexWrap: 'wrap', gap: space.sm }}>
            {SHELF_FINISHES.map((name) => {
              const selected = finish === name;
              return (
                <Pressable
                  key={name}
                  role="radio"
                  aria-checked={selected}
                  accessibilityLabel={t(`shelf.finishes.${name}`)}
                  onPress={() => change({ finish: name })}
                  style={({ pressed }) => ({
                    flexGrow: 1,
                    flexBasis: '30%',
                    padding: selected ? space.sm - 1 : space.sm,
                    gap: space.sm,
                    borderRadius: radius.md,
                    borderWidth: selected ? 2 : 1,
                    borderColor: selected ? colors.accent : colors.border,
                    backgroundColor: colors.background,
                    opacity: pressed ? 0.8 : 1,
                  })}
                >
                  <View aria-hidden>
                    <View style={{ flexDirection: 'row', alignItems: 'flex-end', gap: 1, paddingHorizontal: 6, marginBottom: -1, zIndex: 1 }}>
                      {SAMPLE_BOOKS.map((height, i) => (
                        <View key={height} style={{ width: 9, height, borderTopLeftRadius: 1, borderTopRightRadius: 1, backgroundColor: colors.people[i] }} />
                      ))}
                    </View>
                    <Plank material={shelfMaterials[name]} thickness={Math.max(5, Math.round(style.thickness * 0.7))} radius={style.radius} />
                  </View>
                  <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', minHeight: 18 }}>
                    <Text style={{ color: colors.text, fontSize: fontSize.sm, fontWeight: '600' }}>{t(`shelf.finishes.${name}`)}</Text>
                    {selected && <Check size={14} color={colors.accent} strokeWidth={2.5} aria-hidden />}
                  </View>
                </Pressable>
              );
            })}
          </View>
          <Hint>{t('shelf.followsYou')}</Hint>
        </View>
      </View>
    </Sheet>
  );
}
